import { Link } from "react-router-dom";
import { MapPin, Sparkles } from "lucide-react";

// PlugitAI · Priority Markets Nav
//
// Aug 2026 SEO indexing recovery: a clean useful navigation section (NOT a
// spammy keyword footer) surfacing the internal-link pyramid Kit approved.
//
// Structure:
//   Homepage
//     ↓
//   7 Industry hubs           ← linked once here
//     ↓
//   10 Priority DFW cities × 5 top industries = 50 hand-rewritten combos
//
// Only priority (indexed) combos are shown here so the block doesn't leak
// PageRank to noindex pages.

const PRIORITY_CITIES = [
  { slug: "dallas", name: "Dallas" },
  { slug: "fort-worth", name: "Fort Worth" },
  { slug: "plano", name: "Plano" },
  { slug: "frisco", name: "Frisco" },
  { slug: "arlington", name: "Arlington" },
  { slug: "mckinney", name: "McKinney" },
  { slug: "denton", name: "Denton" },
  { slug: "irving", name: "Irving" },
  { slug: "grapevine", name: "Grapevine" },
  { slug: "southlake", name: "Southlake" },
];

const PRIORITY_INDUSTRIES = [
  { slug: "contractors", name: "Contractors" },
  { slug: "roofers", name: "Roofers" },
  { slug: "attorneys", name: "Attorneys" },
  { slug: "auto-shops", name: "Auto Repair" },
  { slug: "medical-clinics", name: "Medical Clinics" },
];

const ALL_INDUSTRIES = [
  ...PRIORITY_INDUSTRIES,
  { slug: "salons", name: "Salons" },
  { slug: "restaurants", name: "Restaurants" },
];

export default function PriorityMarketsNav() {
  return (
    <nav
      aria-label="Priority DFW markets we build for"
      className="relative border-t border-slate-200"
      style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #F6F8FC 100%)" }}
      data-testid="priority-markets-nav"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
        <div className="text-[11px] uppercase tracking-[0.28em] font-black text-blue-600 mb-3">
          Priority DFW markets · Aug 2026
        </div>
        <h2
          className="font-black tracking-[-0.02em] text-slate-900 mb-2"
          style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
        >
          Where PlugitAI works
        </h2>
        <p className="text-sm md:text-base text-slate-600 max-w-[680px] mb-10">
          Ten cities. Seven industries. Fifty pages of local depth — each written for the businesses that actually work here, not a template swap.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-10">
          {/* Column 1 — Industry hubs (all 7) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-black text-slate-400 mb-4">
              Industries
            </div>
            <ul className="space-y-2">
              {ALL_INDUSTRIES.map((ind) => (
                <li key={ind.slug}>
                  <Link
                    to={`/for/${ind.slug}`}
                    className="group inline-flex items-center gap-2 text-sm font-black text-slate-900 hover:text-blue-600 transition-colors"
                    data-testid={`pmn-industry-${ind.slug}`}
                  >
                    <Sparkles size={12} className="text-blue-600 group-hover:text-blue-700" />
                    {ind.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 2 — 10 cities × 5 priority industries (50 links, organized) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-black text-slate-400 mb-4">
              Priority cities × top industries
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {PRIORITY_CITIES.map((city) => (
                <div key={city.slug} data-testid={`pmn-city-block-${city.slug}`}>
                  <Link
                    to={`/tx/${city.slug}`}
                    className="inline-flex items-center gap-1.5 text-sm font-black text-slate-900 hover:text-blue-600 transition-colors mb-2"
                    data-testid={`pmn-city-${city.slug}`}
                  >
                    <MapPin size={12} className="text-blue-600" />
                    {city.name}
                  </Link>
                  <ul className="space-y-1 pl-4 border-l border-slate-200">
                    {PRIORITY_INDUSTRIES.map((ind) => (
                      <li key={ind.slug}>
                        <Link
                          to={`/for/${ind.slug}/${city.slug}`}
                          className="text-[13px] text-slate-500 hover:text-blue-600 transition-colors"
                          data-testid={`pmn-combo-${ind.slug}-${city.slug}`}
                        >
                          {ind.name} in {city.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
